import type { ExchangeAccount } from '@/api/types';

export type SystemMode = 'LIVE' | 'PAPER' | 'NONE';

export interface ModeSummary {
  mode: SystemMode;
  liveAccounts: number;
  simulatedAccounts: number;
  tradingAccounts: number;
}

/**
 * Decide what the banner says.
 *
 * The rule is that the banner always states the most dangerous truth. If even
 * one live account is enabled, the header reads LIVE, regardless of how many
 * paper accounts are running alongside it. Showing "PAPER" while real money is
 * at risk is the single worst thing this interface could do, so the comparison
 * is deliberately not "most accounts" or "the selected account".
 */
export function deriveMode(accounts: ExchangeAccount[] | undefined): ModeSummary {
  const enabled = (accounts ?? []).filter((account) => account.is_enabled);
  const live = enabled.filter((account) => account.mode === 'LIVE');
  const simulated = enabled.filter((account) => account.mode !== 'LIVE');
  const trading = enabled.filter((account) => account.trading_state === 'RUNNING');

  return {
    mode: live.length > 0 ? 'LIVE' : simulated.length > 0 ? 'PAPER' : 'NONE',
    liveAccounts: live.length,
    simulatedAccounts: simulated.length,
    tradingAccounts: trading.length,
  };
}

export function modeLabel(mode: SystemMode): string {
  switch (mode) {
    case 'LIVE':
      return 'LIVE TRADING ACTIVE';
    case 'PAPER':
      return 'PAPER';
    default:
      return 'NO ACCOUNTS ENABLED';
  }
}

export function modeDetail(summary: ModeSummary): string {
  switch (summary.mode) {
    case 'LIVE':
      return `${summary.liveAccounts} live ${
        summary.liveAccounts === 1 ? 'account' : 'accounts'
      } enabled, ${summary.tradingAccounts} trading. Orders place real money.`;
    case 'PAPER':
      return `${summary.simulatedAccounts} simulated ${
        summary.simulatedAccounts === 1 ? 'account' : 'accounts'
      } enabled. No real orders are placed.`;
    default:
      return 'Enable an exchange account to begin.';
  }
}
